import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {HeaderPrefix, PeriodFilter} from 'src/components/filters';
import {SortDirection, SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';

import {SortHeaderFilter} from '@pb/kit-component-filters';

import {COLUMNS, HEADERS, SELECTION_KEY} from '../../constants';
import {useGetUserSearchResultLastActive} from '../../hooks/useGetUserSearchResultLastActive';
import {
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from '../../slices/user-search-result-state.slice';
import {userSearchResultResultSelector} from '../../slices/user-search-result.slice';

import {HeaderSkeleton} from './header-skeleton';

type Props = {
    userIds: number[],
    totalCount: number,
};

const LastMobileActiveHeaderController = ({userIds, totalCount}: Props) => {
    const dispatch = useDispatch();

    const cacheKeys = useSelector(userSearchResultStateSelector);
    const {isLoading, loadingError} = useGetUserSearchResultLastActive({
        userIds,
        totalCount,
        columnId: COLUMNS.LAST_MOBILE_ACTIVITY,
    });

    const [startDate, setStartDate] = useState(cacheKeys.filters.mobileLastActivityStart as string | undefined);
    const [endDate, setEndDate] = useState(cacheKeys.filters.mobileLastActivityEnd as string | undefined);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: UserSortFields.LAST_MOBILE_ACTIVITY,
        }));
    }, [dispatch]);

    const applyFilter = useCallback(() => {
        const {
            mobileLastActivityStart: _startDate,
            mobileLastActivityEnd: _endDate,
            ...otherFilters
        } = cacheKeys.filters;

        const filters = {
            ...otherFilters,
            ...startDate ? {mobileLastActivityStart: startDate} : undefined,
            ...endDate ? {mobileLastActivityEnd: endDate} : undefined,
        };

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setFilters(filters));
    }, [cacheKeys.filters, startDate, endDate, dispatch]);

    const clearFilter = useCallback(() => {
        const {
            mobileLastActivityStart: _startDate,
            mobileLastActivityEnd: _endDate,
            ...filters
        } = cacheKeys.filters;

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setFilters(filters));
    }, [cacheKeys.filters, dispatch]);

    const cancelFilter = useCallback(() => {
        setStartDate(cacheKeys.filters.mobileLastActivityStart);
        setEndDate(cacheKeys.filters.mobileLastActivityEnd);
    }, [cacheKeys.filters, setStartDate, setEndDate]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters.mobileLastActivityStart || !!cacheKeys.filters.mobileLastActivityEnd}
            sortable={true}
            contentBefore={(isLoading || loadingError)
                ? <HeaderPrefix isLoading={isLoading} loadingError={loadingError}/>
                : undefined}
            caption={HEADERS[COLUMNS.LAST_MOBILE_ACTIVITY]}
            direction={cacheKeys.sortCriteria.field === UserSortFields.LAST_MOBILE_ACTIVITY
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <PeriodFilter
                startDate={startDate}
                endDate={endDate}
                onChangeStartDate={setStartDate}
                onChangeEndDate={setEndDate}
            />
        </SortHeaderFilter>
    );
};

export const LastMobileActiveHeader = () => {
    const result = useSelector(userSearchResultResultSelector);

    return result ? (
        <LastMobileActiveHeaderController
            userIds={result.data.map(({id}) => id)}
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.LAST_MOBILE_ACTIVITY}/>
    );
};
